import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';
import { Input, Textarea } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { FormRow } from '../../components/ui/FormRow';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { DataTable, type Column } from '../../components/DataTable';
import { Tabs } from '../../components/ui/Tabs';
import { fmt } from '../../lib/formatters';
import { useVolver } from '../../lib/useVolver';
import { confirmar } from '../../lib/toast';

type IncidenciaRow = {
  id: string;
  fecha: string;
  descripcion: string;
  estado: 'PDTE_RESOLVER' | 'COMUNICAR' | 'CERRADA';
};

type NotaRow = {
  id: string;
  fecha: string;
  descripcion: string;
};

type ProductoAsociado = {
  id: string;
  sku: string;
  nombre: string;
  estado: string;
  cliente_codigo: string | null;
  cliente_nombre: string | null;
};

type ContactoRow = {
  id: string;
  nombre: string | null;
  cargo: string | null;
  email: string | null;
  telefono: string | null;
  es_principal: boolean;
  orden: number;
};

type ProductoDelProveedor = {
  id: string;
  ref_proveedor: string;
  nombre: string;
  descripcion: string | null;
  coste_usd: number | string | null;
  moq: number | null;
  foc_pct: number | string | null;
  sample: string | null;
  origen: string | null;
  created_at: string;
  activo: boolean;
  gama_id: string | null;
  categoria_id: string | null;
  subcategoria_id: string | null;
  detalle_id: string | null;
  gama_nombre: string | null;
  categoria_nombre: string | null;
  subcategoria_nombre: string | null;
  detalle_nombre: string | null;
  imagen_storage_path: string | null;
};

type PedidoAsociado = {
  id: string;
  fecha: string;
  unidades: number;
  estado: string;
  estado_nombre: string | null;
  estado_color: string | null;
  ref_externa: string | null;
  fecha_estimada_llegada: string | null;
  fecha_recepcion: string | null;
  producto_sku: string;
  producto_nombre: string;
};

type ProveedorFull = {
  id: string;
  nombre: string;
  tipo_producto: string | null;
  contacto_principal: string | null;
  email: string | null;
  telefono: string | null;
  whatsapp: string | null;
  web: string | null;
  direccion: string | null;
  pais: string | null;
  emails_cc: string[];
  foc_pct: number;
  production_time_dias: number;
  payment_deposit_pct: number;
  payment_transfer_pct: number;
  notas: string | null;
  activo: boolean;
  incidencias: IncidenciaRow[];
  notas_log: NotaRow[];
  productos: ProductoAsociado[];
  productos_proveedor: ProductoDelProveedor[];
  pedidos: PedidoAsociado[];
  contactos: ContactoRow[];
};

type EmailTemplate = {
  id: string;
  nombre: string;
  asunto: string;
  cuerpo: string;
  destinatario_default: string | null;
  emails_cc_default: string[];
  activo: boolean;
};

const ESTADO_BADGE: Record<IncidenciaRow['estado'], { color: string; label: string }> = {
  PDTE_RESOLVER: { color: '#f59e0b', label: 'Pdte. resolver' },
  COMUNICAR:     { color: '#3b82f6', label: 'Comunicar' },
  CERRADA:       { color: '#10b981', label: 'Cerrada' },
};

export function ProveedorDetalle() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const volver = useVolver('/proveedores/listado');
  const qc = useQueryClient();

  const proveedor = useQuery({
    queryKey: ['proveedor', id, 'full'],
    queryFn: () => api.get<ProveedorFull>(`/proveedores/${id}/full`),
    enabled: !!id,
  });

  const [editOpen, setEditOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [draft, setDraft] = useState<Partial<ProveedorFull> & { emails_cc_csv?: string }>({});
  const [tab, setTab] = useState<'datos' | 'productos' | 'pedidos'>('datos');

  const updateProv = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.put(`/proveedores/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['proveedor', id, 'full'] }),
  });

  if (proveedor.isLoading) return <Spinner />;
  if (proveedor.isError || !proveedor.data) {
    return (
      <div>
        <PageHeader title="Proveedor no encontrado" />
        <Button variant="secondary" onClick={volver}>← Volver</Button>
      </div>
    );
  }
  const p = proveedor.data;

  const openEdit = () => {
    setDraft({
      nombre: p.nombre,
      tipo_producto: p.tipo_producto ?? '',
      contacto_principal: p.contacto_principal ?? '',
      email: p.email ?? '',
      telefono: p.telefono ?? '',
      whatsapp: p.whatsapp ?? '',
      web: p.web ?? '',
      direccion: p.direccion ?? '',
      pais: p.pais ?? 'CN',
      foc_pct: p.foc_pct,
      production_time_dias: p.production_time_dias,
      payment_deposit_pct: p.payment_deposit_pct,
      payment_transfer_pct: p.payment_transfer_pct,
      activo: p.activo,
      emails_cc_csv: (p.emails_cc ?? []).join(', '),
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    const body: Record<string, unknown> = { ...draft };
    body.emails_cc = draft.emails_cc_csv ?? '';
    delete (body as { emails_cc_csv?: string }).emails_cc_csv;
    await updateProv.mutateAsync(body);
    setEditOpen(false);
  };

  return (
    <div>
      <PageHeader
        title={p.nombre}
        subtitle={`${p.tipo_producto ?? '—'} · ${p.pais ?? '—'}${p.activo ? '' : ' · inactivo'}`}
        actions={
          <>
            <Button variant="secondary" onClick={volver}>← Volver</Button>
            <Button variant="create" onClick={() => navigate(`/proveedores/productos/nuevo?proveedor=${p.id}`)}>
              + Nuevo producto
            </Button>
            <Button variant="secondary" onClick={() => setEmailOpen(true)} disabled={!p.email}>
              Enviar email
            </Button>
            <Button variant="edit" onClick={openEdit}>✏️ Editar</Button>
          </>
        }
      />

      <Tabs
        active={tab}
        onChange={(k) => setTab(k as typeof tab)}
        tabs={[
          { key: 'datos', label: 'Datos' },
          { key: 'productos', label: 'Productos', badge: p.productos_proveedor?.length ?? 0 },
          { key: 'pedidos', label: 'Pedidos', badge: p.pedidos?.length ?? 0 },
        ]}
      />

      {tab === 'datos' && (<>
      <div className="mb-6 grid gap-4 md:grid-cols-12">
        <Card title="Identidad" className="md:col-span-5">
          <dl className="space-y-1.5 text-sm">
            <Row k="Nombre">{p.nombre}</Row>
            <Row k="Tipo producto"><span className="text-slate-700">{p.tipo_producto ?? '—'}</span></Row>
            <Row k="Web">
              {p.web
                ? <a href={p.web} target="_blank" rel="noreferrer" className="cursor-pointer text-sky-600 hover:underline">{p.web}</a>
                : '—'}
            </Row>
            <Row k="Dirección"><span className="text-right text-slate-700">{p.direccion ?? '—'}</span></Row>
            <Row k="Persona contacto">{p.contacto_principal ?? '—'}</Row>
            <Row k="Email">
              {p.email
                ? <a href={`mailto:${p.email}`} className="cursor-pointer text-sky-600 hover:underline">{p.email}</a>
                : '—'}
            </Row>
            <Row k="Teléfono">{p.telefono ?? '—'}</Row>
            <Row k="WhatsApp">{p.whatsapp ?? '—'}</Row>
            <Row k="País">{p.pais ?? '—'}</Row>
            <Row k="Emails CC">
              {p.emails_cc?.length > 0
                ? <span className="text-right text-slate-700">{p.emails_cc.join(', ')}</span>
                : <span className="text-slate-400">—</span>}
            </Row>
          </dl>
        </Card>

        <Card title="Condiciones" className="md:col-span-3">
          <dl className="space-y-1.5 text-sm">
            <Row k="F.O.C.">{fmt.pct(p.foc_pct)}</Row>
            <Row k="Production time"><span className="font-medium">{p.production_time_dias} días</span></Row>
          </dl>
        </Card>

        <Card title="Payment Terms" className="md:col-span-4">
          <dl className="space-y-1.5 text-sm">
            <Row k="Deposit">{fmt.pct(p.payment_deposit_pct)}</Row>
            <Row k="Transfer post-inspection">{fmt.pct(p.payment_transfer_pct)}</Row>
          </dl>
        </Card>
      </div>

      <div className="mb-6">
        <ContactosCard proveedorId={p.id} contactos={p.contactos ?? []} />
      </div>

      <div className="mb-6">
        <NotasCard proveedorId={p.id} notas={p.notas_log ?? []} />
      </div>

      <div className="mb-6">
        <IncidenciasCard proveedorId={p.id} incidencias={p.incidencias} />
      </div>
      </>)}

      {tab === 'productos' && (
        <ProductosDelProveedorCard
          proveedorId={p.id}
          proveedorNombre={p.nombre}
          productos={p.productos_proveedor ?? []}
          productosCliente={p.productos ?? []}
        />
      )}

      {tab === 'pedidos' && (
        <Card title={`Pedidos a ${p.nombre} (${p.pedidos.length})`}>
          {p.pedidos.length === 0 ? (
            <p className="text-sm italic text-slate-400">Sin pedidos a este proveedor.</p>
          ) : (
            <PedidosTable rows={p.pedidos} />
          )}
        </Card>
      )}

      {/* Modal de edición */}
      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Editar proveedor"
        size="xl"
        footer={
          <>
            <Button variant="danger" onClick={() => setEditOpen(false)}>Cancelar</Button>
            <Button variant="save" onClick={submitEdit} disabled={updateProv.isPending}>Guardar</Button>
          </>
        }
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <FormRow label="Nombre" required><Input value={(draft.nombre as string) ?? ''} onChange={(e) => setDraft((d) => ({ ...d, nombre: e.target.value }))} /></FormRow>
          <FormRow label="Tipo de producto"><Input value={(draft.tipo_producto as string) ?? ''} onChange={(e) => setDraft((d) => ({ ...d, tipo_producto: e.target.value }))} placeholder="CARCASAS, HDD, USB, SSD…" /></FormRow>
          <FormRow label="Persona contacto"><Input value={(draft.contacto_principal as string) ?? ''} onChange={(e) => setDraft((d) => ({ ...d, contacto_principal: e.target.value }))} /></FormRow>
          <FormRow label="Email"><Input value={(draft.email as string) ?? ''} onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))} /></FormRow>
          <FormRow label="Teléfono"><Input value={(draft.telefono as string) ?? ''} onChange={(e) => setDraft((d) => ({ ...d, telefono: e.target.value }))} /></FormRow>
          <FormRow label="WhatsApp"><Input value={(draft.whatsapp as string) ?? ''} onChange={(e) => setDraft((d) => ({ ...d, whatsapp: e.target.value }))} /></FormRow>
          <FormRow label="Web"><Input value={(draft.web as string) ?? ''} onChange={(e) => setDraft((d) => ({ ...d, web: e.target.value }))} placeholder="https://…" /></FormRow>
          <FormRow label="País"><Input value={(draft.pais as string) ?? ''} onChange={(e) => setDraft((d) => ({ ...d, pais: e.target.value }))} /></FormRow>
          <div className="sm:col-span-2">
            <FormRow label="Dirección"><Textarea rows={2} value={(draft.direccion as string) ?? ''} onChange={(e) => setDraft((d) => ({ ...d, direccion: e.target.value }))} /></FormRow>
          </div>
          <div className="sm:col-span-2">
            <FormRow label="Emails CC (separados por coma)" hint="Se añadirán al CC cuando uses 'Enviar email'.">
              <Input value={draft.emails_cc_csv ?? ''} onChange={(e) => setDraft((d) => ({ ...d, emails_cc_csv: e.target.value }))} placeholder="otra@empresa.com, soporte@proveedor.com" />
            </FormRow>
          </div>
          <FormRow label="F.O.C. (%)"><Input type="number" step="any" value={(draft.foc_pct as number) ?? 1} onChange={(e) => setDraft((d) => ({ ...d, foc_pct: Number(e.target.value) }))} /></FormRow>
          <FormRow label="Production time (días)"><Input type="number" value={(draft.production_time_dias as number) ?? 35} onChange={(e) => setDraft((d) => ({ ...d, production_time_dias: Number(e.target.value) }))} /></FormRow>
          <FormRow label="Payment Deposit (%)"><Input type="number" step="any" value={(draft.payment_deposit_pct as number) ?? 10} onChange={(e) => setDraft((d) => ({ ...d, payment_deposit_pct: Number(e.target.value) }))} /></FormRow>
          <FormRow label="Payment Transfer post-inspection (%)"><Input type="number" step="any" value={(draft.payment_transfer_pct as number) ?? 90} onChange={(e) => setDraft((d) => ({ ...d, payment_transfer_pct: Number(e.target.value) }))} /></FormRow>
          <FormRow label="Activo">
            <Select
              options={[{ value: 'true', label: 'Sí' }, { value: 'false', label: 'No' }]}
              value={String(draft.activo ?? true)}
              emptyLabel={null}
              onChange={(e) => setDraft((d) => ({ ...d, activo: e.target.value === 'true' }))}
            />
          </FormRow>
        </div>
      </Modal>

      <EmailModal proveedor={p} open={emailOpen} onClose={() => setEmailOpen(false)} />
    </div>
  );
}

function Row({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-slate-500">{k}</dt>
      <dd className="text-slate-900 text-right max-w-[60%] break-words">{children}</dd>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Productos del proveedor (catálogo del propio proveedor)
// ─────────────────────────────────────────────────────────────────────

const SAMPLE_BADGE: Record<string, { color: string; label: string }> = {
  SI:          { color: '#10b981', label: 'Sí' },
  SOLICITADO:  { color: '#f59e0b', label: 'Solicitado' },
  NO:          { color: '#94a3b8', label: 'No' },
  PENDIENTE:   { color: '#f59e0b', label: 'Pendiente' },
};

function ProductosDelProveedorCard({
  proveedorId,
  proveedorNombre,
  productos,
  productosCliente,
}: {
  proveedorId: string;
  proveedorNombre: string;
  productos: ProductoDelProveedor[];
  productosCliente: ProductoAsociado[];
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Firmar miniaturas en bulk (mismo patrón que CatalogoList).
  const paths = useMemo(
    () => productos.map((r) => r.imagen_storage_path).filter((p): p is string => Boolean(p)),
    [productos],
  );
  const imagenes = useQuery({
    queryKey: ['proveedor-productos', proveedorId, 'firmar-imagenes', paths],
    queryFn: () => api.post<{ urls: Record<string, string | null> }>(
      '/productos-proveedor/firmar-imagenes-bulk',
      { paths },
    ),
    enabled: paths.length > 0,
    staleTime: 30 * 60 * 1000,
  });
  const [zoomFoto, setZoomFoto] = useState<{ url: string; nombre: string } | null>(null);

  // Catálogos para filtros + bulk (mismas listas que CatalogoList).
  const gamas = useQuery({
    queryKey: ['list', '/admin/gamas'],
    queryFn: () => api.get<{ data: { id: string; nombre: string }[] }>('/admin/gamas'),
  });
  const categorias = useQuery({
    queryKey: ['list', '/admin/categorias'],
    queryFn: () => api.get<{ data: { id: string; nombre: string }[] }>('/admin/categorias'),
  });
  const subcategorias = useQuery({
    queryKey: ['list', '/admin/subcategorias'],
    queryFn: () => api.get<{ data: { id: string; nombre: string; categoria_id: string }[] }>('/admin/subcategorias'),
  });
  const detalles = useQuery({
    queryKey: ['list', '/admin/detalles'],
    queryFn: () => api.get<{ data: { id: string; nombre: string; subcategoria_id: string; activo: boolean }[] }>('/admin/detalles'),
  });

  // Selección múltiple + acciones bulk.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkGama, setBulkGama] = useState('');
  const [bulkCategoria, setBulkCategoria] = useState('');
  const [bulkSubcategoria, setBulkSubcategoria] = useState('');
  const [bulkDetalle, setBulkDetalle] = useState('');
  const [bulkSample, setBulkSample] = useState('');
  const subcatsBulk = useMemo(() => {
    const all = subcategorias.data?.data ?? [];
    if (!bulkCategoria || bulkCategoria === '__none__') return all;
    return all.filter((s) => s.categoria_id === bulkCategoria);
  }, [bulkCategoria, subcategorias.data]);
  const detallesBulk = useMemo(() => {
    const all = (detalles.data?.data ?? []).filter((d) => d.activo);
    if (!bulkSubcategoria || bulkSubcategoria === '__none__') return all;
    return all.filter((d) => d.subcategoria_id === bulkSubcategoria);
  }, [bulkSubcategoria, detalles.data]);

  const bulkMut = useMutation({
    mutationFn: (updates: Record<string, unknown>) =>
      api.patch<{ updated: number }>('/productos-proveedor/bulk', {
        ids: Array.from(selectedIds),
        updates,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['proveedor', proveedorId, 'full'] });
      qc.invalidateQueries({ queryKey: ['catalogo'] });
      setSelectedIds(new Set());
      setBulkGama(''); setBulkCategoria(''); setBulkSubcategoria(''); setBulkDetalle(''); setBulkSample('');
    },
  });
  const bulkDeleteMut = useMutation({
    mutationFn: () =>
      api.post<{ deleted: number }>('/productos-proveedor/bulk-delete', {
        ids: Array.from(selectedIds),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['proveedor', proveedorId, 'full'] });
      qc.invalidateQueries({ queryKey: ['catalogo'] });
      setSelectedIds(new Set());
    },
  });
  const applyBulk = async () => {
    const updates: Record<string, unknown> = {};
    if (bulkGama) updates.gama_id = bulkGama === '__none__' ? null : bulkGama;
    if (bulkCategoria) updates.categoria_id = bulkCategoria === '__none__' ? null : bulkCategoria;
    if (bulkSubcategoria) updates.subcategoria_id = bulkSubcategoria === '__none__' ? null : bulkSubcategoria;
    if (bulkDetalle) updates.detalle_id = bulkDetalle === '__none__' ? null : bulkDetalle;
    if (bulkSample) updates.sample = bulkSample === '__none__' ? null : bulkSample;
    if (Object.keys(updates).length === 0) return;
    if (!(await confirmar({ title: `¿Aplicar cambios a ${selectedIds.size} producto(s)?`, confirmLabel: 'Aplicar', danger: true }))) return;
    bulkMut.mutate(updates);
  };
  const numSeleccionados = selectedIds.size;
  const noneOpt = { value: '__none__', label: '— quitar valor —' };

  const cols: Column<ProductoDelProveedor>[] = [
    { key: 'created_at', label: 'Fecha', width: '7rem', render: (r) => fmt.date(r.created_at), sortValue: (r) => r.created_at },
    { key: 'origen', label: 'Origen', width: '8rem', render: (r) => r.origen ?? <span className="text-slate-300">—</span>, sortValue: (r) => r.origen ?? '' },
    { key: 'ref_proveedor', label: 'Ref.', render: (r) => <span className="font-mono text-xs">{r.ref_proveedor}</span>, sortValue: (r) => r.ref_proveedor },
    {
      key: '_foto',
      label: 'Foto',
      sortable: false,
      width: '4rem',
      render: (r) => {
        const url = r.imagen_storage_path ? imagenes.data?.urls?.[r.imagen_storage_path] : null;
        if (!url) return <span className="text-slate-300">—</span>;
        return (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setZoomFoto({ url, nombre: r.nombre }); }}
            className="block h-8 w-8 cursor-pointer overflow-hidden rounded border border-slate-200 bg-white hover:border-slate-400"
            title="Ver foto"
          >
            <img src={url} alt={r.nombre} className="h-full w-full object-cover" loading="lazy" />
          </button>
        );
      },
    },
    { key: 'nombre', label: 'Nombre', render: (r) => <span className="block max-w-[18rem] truncate" title={r.nombre}>{r.nombre}</span>, sortValue: (r) => r.nombre },
    { key: 'gama_nombre', label: 'Gama', render: (r) => r.gama_nombre ?? '—', sortValue: (r) => r.gama_nombre ?? '' },
    { key: 'subcategoria_nombre', label: 'Subtipo', render: (r) => r.subcategoria_nombre ?? '—', sortValue: (r) => r.subcategoria_nombre ?? '' },
    { key: 'detalle_nombre', label: 'Detalle', render: (r) => r.detalle_nombre ?? <span className="text-slate-300">—</span>, sortValue: (r) => r.detalle_nombre ?? '' },
    {
      key: 'sample',
      label: 'Sample',
      render: (r) => {
        const s = r.sample ?? '';
        const b = SAMPLE_BADGE[s];
        return b ? <Badge color={b.color}>{b.label}</Badge> : <span className="text-slate-300">—</span>;
      },
      sortValue: (r) => r.sample ?? '',
    },
    { key: 'coste_usd', label: 'Precio', align: 'right', render: (r) => fmt.usd(r.coste_usd), sortValue: (r) => Number(r.coste_usd) },
    { key: 'moq', label: 'MOQ', align: 'right', render: (r) => fmt.int(r.moq), sortValue: (r) => Number(r.moq) },
    { key: 'foc_pct', label: 'F.O.C.', align: 'right', render: (r) => r.foc_pct != null ? fmt.pct(r.foc_pct) : '—', sortValue: (r) => Number(r.foc_pct) },
  ];

  // Opciones de filtros derivadas del catálogo del proveedor (solo lo presente).
  const filterGamaOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const p of productos) if (p.gama_id && p.gama_nombre) seen.set(p.gama_id, p.gama_nombre);
    return Array.from(seen.entries()).map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [productos]);
  const filterSubcatOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const p of productos) if (p.subcategoria_id && p.subcategoria_nombre) seen.set(p.subcategoria_id, p.subcategoria_nombre);
    return Array.from(seen.entries()).map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [productos]);
  const filterDetalleOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const p of productos) if (p.detalle_id && p.detalle_nombre) seen.set(p.detalle_id, p.detalle_nombre);
    return Array.from(seen.entries()).map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [productos]);

  return (
    <div className="space-y-6">
      <Card
        title={`Productos del proveedor (${productos.length})`}
        actions={
          <Button
            variant="create"
            size="sm"
            onClick={() => navigate(`/proveedores/productos/nuevo?proveedor=${proveedorId}`)}
          >
            + Nuevo producto
          </Button>
        }
      >
        {productos.length === 0 ? (
          <p className="text-sm italic text-slate-400">
            Este proveedor aún no tiene productos en el catálogo.
          </p>
        ) : (
          <>
            {numSeleccionados > 0 && (
              <div className="mb-3 rounded-md border border-sky-200 bg-sky-50 p-3">
                <div className="mb-2 flex items-center justify-between text-sm">
                  <span className="font-semibold text-sky-900">
                    {numSeleccionados} producto{numSeleccionados === 1 ? '' : 's'} seleccionado{numSeleccionados === 1 ? '' : 's'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedIds(new Set())}
                    className="cursor-pointer text-xs text-sky-700 hover:text-sky-900 hover:underline"
                  >
                    Limpiar selección
                  </button>
                </div>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-6">
                  <div>
                    <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">Gama</label>
                    <Select
                      options={[noneOpt, ...(gamas.data?.data ?? []).map((g) => ({ value: g.id, label: g.nombre }))]}
                      value={bulkGama}
                      onChange={(e) => setBulkGama(e.target.value)}
                      emptyLabel="(sin cambio)"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">Categoría</label>
                    <Select
                      options={[noneOpt, ...(categorias.data?.data ?? []).map((c) => ({ value: c.id, label: c.nombre }))]}
                      value={bulkCategoria}
                      onChange={(e) => { setBulkCategoria(e.target.value); setBulkSubcategoria(''); setBulkDetalle(''); }}
                      emptyLabel="(sin cambio)"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">Subtipo</label>
                    <Select
                      options={[noneOpt, ...subcatsBulk.map((s) => ({ value: s.id, label: s.nombre }))]}
                      value={bulkSubcategoria}
                      onChange={(e) => { setBulkSubcategoria(e.target.value); setBulkDetalle(''); }}
                      emptyLabel="(sin cambio)"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">Detalle</label>
                    <Select
                      options={[noneOpt, ...detallesBulk.map((d) => ({ value: d.id, label: d.nombre }))]}
                      value={bulkDetalle}
                      onChange={(e) => setBulkDetalle(e.target.value)}
                      emptyLabel="(sin cambio)"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">Sample</label>
                    <Select
                      options={[
                        noneOpt,
                        { value: 'SOLICITADO', label: 'Solicitado' },
                        { value: 'SI', label: 'Sí' },
                        { value: 'NO', label: 'No' },
                      ]}
                      value={bulkSample}
                      onChange={(e) => setBulkSample(e.target.value)}
                      emptyLabel="(sin cambio)"
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <Button variant="save" onClick={applyBulk} disabled={bulkMut.isPending} className="w-full">
                      {bulkMut.isPending ? 'Aplicando…' : 'Aplicar'}
                    </Button>
                    <Button
                      variant="danger"
                      onClick={async () => {
                        if (!(await confirmar({ title: `¿Eliminar ${numSeleccionados} producto(s)?`, confirmLabel: 'Eliminar', danger: true }))) return;
                        bulkDeleteMut.mutate();
                      }}
                      disabled={bulkDeleteMut.isPending}
                      className="w-full"
                    >
                      {bulkDeleteMut.isPending ? 'Eliminando…' : '🗑'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <DataTable<ProductoDelProveedor>
              columns={cols}
              rows={productos}
              rowKey={(r) => r.id}
              onRowClick={(r) => navigate(`/proveedores/productos/${r.id}`)}
              defaultSort={{ key: 'created_at', desc: true }}
              searchPlaceholder="Buscar por ref, nombre, subtipo, detalle…"
              storageKey="productos"
              selectable
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              filters={[
                { key: 'gama_id', label: 'Gama', options: filterGamaOptions },
                { key: 'subcategoria_id', label: 'Subtipo', options: filterSubcatOptions },
                { key: 'detalle_id', label: 'Detalle', options: filterDetalleOptions },
                { key: 'sample', label: 'Sample', options: [
                  { value: 'SI', label: 'Sí' },
                  { value: 'SOLICITADO', label: 'Solicitado' },
                  { value: 'NO', label: 'No' },
                ] },
              ]}
            />
          </>
        )}
      </Card>

      <Modal open={!!zoomFoto} onClose={() => setZoomFoto(null)} title={zoomFoto?.nombre ?? ''} size="lg">
        {zoomFoto && (
          <img
            src={zoomFoto.url}
            alt={zoomFoto.nombre}
            className="mx-auto max-h-[70vh] w-auto rounded border border-slate-200 object-contain"
          />
        )}
      </Modal>

      {productosCliente.length > 0 && (
        <Card title={`Productos de clientes que apuntan a ${proveedorNombre} (${productosCliente.length})`}>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-2 py-1.5 text-left">SKU</th>
                  <th className="px-2 py-1.5 text-left">Nombre</th>
                  <th className="px-2 py-1.5 text-left">Cliente</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {productosCliente.map((pr) => (
                  <tr
                    key={pr.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/game/productos/${pr.id}`)}
                  >
                    <td className="px-2 py-1 font-mono text-xs">{pr.sku}</td>
                    <td className="px-2 py-1 text-xs">{pr.nombre}</td>
                    <td className="px-2 py-1 text-xs">{pr.cliente_codigo ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Incidencias
// ─────────────────────────────────────────────────────────────────────

function IncidenciasCard({ proveedorId, incidencias }: { proveedorId: string; incidencias: IncidenciaRow[] }) {
  const qc = useQueryClient();
  const [nuevaOpen, setNuevaOpen] = useState(false);
  const [draft, setDraft] = useState<{ fecha: string; descripcion: string; estado: IncidenciaRow['estado'] }>({
    fecha: new Date().toISOString().slice(0, 10),
    descripcion: '',
    estado: 'PDTE_RESOLVER',
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['proveedor', proveedorId, 'full'] });

  const createInc = useMutation({
    mutationFn: () => api.post(`/proveedores/${proveedorId}/incidencias`, draft),
    onSuccess: () => { invalidate(); setNuevaOpen(false); setDraft({ fecha: new Date().toISOString().slice(0, 10), descripcion: '', estado: 'PDTE_RESOLVER' }); },
  });

  const cambiarEstado = useMutation({
    mutationFn: ({ incidenciaId, estado }: { incidenciaId: string; estado: IncidenciaRow['estado'] }) =>
      api.patch(`/proveedores/${proveedorId}/incidencias/${incidenciaId}`, { estado }),
    onSuccess: invalidate,
  });

  const borrar = useMutation({
    mutationFn: (incidenciaId: string) => api.delete(`/proveedores/${proveedorId}/incidencias/${incidenciaId}`),
    onSuccess: invalidate,
  });

  return (
    <Card
      title={`Histórico de incidencias (${incidencias.length})`}
      actions={<Button variant="create" size="sm" onClick={() => setNuevaOpen(true)}>+ Nueva incidencia</Button>}
    >
      {incidencias.length === 0 ? (
        <p className="text-sm italic text-slate-400">Sin incidencias registradas.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-2 py-1.5 text-left">Fecha</th>
                <th className="px-2 py-1.5 text-left">Descripción</th>
                <th className="px-2 py-1.5 text-left">Estado</th>
                <th className="px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {incidencias.map((i) => (
                <tr key={i.id} className="align-top">
                  <td className="px-2 py-1.5 text-xs text-slate-500 whitespace-nowrap">{fmt.date(i.fecha)}</td>
                  <td className="px-2 py-1.5 text-sm">{i.descripcion}</td>
                  <td className="px-2 py-1.5">
                    <Select
                      options={(Object.keys(ESTADO_BADGE) as Array<IncidenciaRow['estado']>).map((k) => ({ value: k, label: ESTADO_BADGE[k].label }))}
                      value={i.estado}
                      emptyLabel={null}
                      onChange={(e) => cambiarEstado.mutate({ incidenciaId: i.id, estado: e.target.value as IncidenciaRow['estado'] })}
                      className="w-44 text-xs"
                    />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <button
                      type="button"
                      onClick={async () => { if (await confirmar({ title: '¿Borrar incidencia?', confirmLabel: 'Borrar', danger: true })) borrar.mutate(i.id); }}
                      className="cursor-pointer text-xs text-rose-600 hover:text-rose-800"
                    >
                      Borrar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={nuevaOpen}
        onClose={() => setNuevaOpen(false)}
        title="Nueva incidencia"
        footer={
          <>
            <Button variant="danger" onClick={() => setNuevaOpen(false)}>Cancelar</Button>
            <Button variant="save" onClick={() => createInc.mutate()} disabled={!draft.descripcion.trim() || createInc.isPending}>Guardar</Button>
          </>
        }
      >
        <div className="space-y-3">
          <FormRow label="Fecha"><Input type="date" value={draft.fecha} onChange={(e) => setDraft((d) => ({ ...d, fecha: e.target.value }))} /></FormRow>
          <FormRow label="Descripción" required><Textarea rows={4} value={draft.descripcion} onChange={(e) => setDraft((d) => ({ ...d, descripcion: e.target.value }))} /></FormRow>
          <FormRow label="Estado">
            <Select
              options={(Object.keys(ESTADO_BADGE) as Array<IncidenciaRow['estado']>).map((k) => ({ value: k, label: ESTADO_BADGE[k].label }))}
              value={draft.estado}
              emptyLabel={null}
              onChange={(e) => setDraft((d) => ({ ...d, estado: e.target.value as IncidenciaRow['estado'] }))}
            />
          </FormRow>
        </div>
      </Modal>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Notas (mismo patrón que Incidencias)
// ─────────────────────────────────────────────────────────────────────

function NotasCard({ proveedorId, notas }: { proveedorId: string; notas: NotaRow[] }) {
  const qc = useQueryClient();
  const [nuevaOpen, setNuevaOpen] = useState(false);
  const [draft, setDraft] = useState<{ fecha: string; descripcion: string }>({
    fecha: new Date().toISOString().slice(0, 10),
    descripcion: '',
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['proveedor', proveedorId, 'full'] });

  const createNota = useMutation({
    mutationFn: () => api.post(`/proveedores/${proveedorId}/notas`, draft),
    onSuccess: () => { invalidate(); setNuevaOpen(false); setDraft({ fecha: new Date().toISOString().slice(0, 10), descripcion: '' }); },
  });

  const borrar = useMutation({
    mutationFn: (notaId: string) => api.delete(`/proveedores/${proveedorId}/notas/${notaId}`),
    onSuccess: invalidate,
  });

  return (
    <Card
      title={`Notas (${notas.length})`}
      actions={<Button variant="create" size="sm" onClick={() => setNuevaOpen(true)}>+ Nueva nota</Button>}
    >
      {notas.length === 0 ? (
        <p className="text-sm italic text-slate-400">Sin notas registradas.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-2 py-1.5 text-left">Fecha</th>
                <th className="px-2 py-1.5 text-left">Descripción</th>
                <th className="px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {notas.map((n) => (
                <tr key={n.id} className="align-top">
                  <td className="px-2 py-1.5 text-xs text-slate-500 whitespace-nowrap">{fmt.date(n.fecha)}</td>
                  <td className="px-2 py-1.5 text-sm whitespace-pre-line">{n.descripcion}</td>
                  <td className="px-2 py-1.5 text-right">
                    <button
                      type="button"
                      onClick={async () => { if (await confirmar({ title: '¿Borrar nota?', confirmLabel: 'Borrar', danger: true })) borrar.mutate(n.id); }}
                      className="cursor-pointer text-xs text-rose-600 hover:text-rose-800"
                    >Borrar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={nuevaOpen}
        onClose={() => setNuevaOpen(false)}
        title="Nueva nota"
        footer={
          <>
            <Button variant="danger" onClick={() => setNuevaOpen(false)}>Cancelar</Button>
            <Button variant="save" onClick={() => createNota.mutate()} disabled={!draft.descripcion.trim() || createNota.isPending}>Guardar</Button>
          </>
        }
      >
        <div className="space-y-3">
          <FormRow label="Fecha"><Input type="date" value={draft.fecha} onChange={(e) => setDraft((d) => ({ ...d, fecha: e.target.value }))} /></FormRow>
          <FormRow label="Descripción" required><Textarea rows={4} value={draft.descripcion} onChange={(e) => setDraft((d) => ({ ...d, descripcion: e.target.value }))} /></FormRow>
        </div>
      </Modal>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Pedidos (lectura)
// ─────────────────────────────────────────────────────────────────────

function PedidosTable({ rows }: { rows: PedidoAsociado[] }) {
  const cols: Column<PedidoAsociado>[] = [
    { key: 'fecha', label: 'Fecha', render: (r) => fmt.date(r.fecha), sortValue: (r) => r.fecha },
    { key: 'producto_sku', label: 'SKU', render: (r) => <span className="font-mono text-xs">{r.producto_sku}</span>, sortValue: (r) => r.producto_sku },
    { key: 'unidades', label: 'Uds.', align: 'right', render: (r) => fmt.int(r.unidades), sortValue: (r) => r.unidades },
    { key: 'estado', label: 'Estado', render: (r) => <Badge color={r.estado_color ?? '#64748b'}>{r.estado_nombre ?? r.estado}</Badge>, sortValue: (r) => r.estado_nombre ?? r.estado },
    { key: 'fecha_estimada_llegada', label: 'ETA', render: (r) => fmt.date(r.fecha_estimada_llegada), sortValue: (r) => r.fecha_estimada_llegada ?? '' },
  ];
  return (
    <DataTable<PedidoAsociado>
      columns={cols}
      rows={rows}
      rowKey={(r) => r.id}
      defaultSort={{ key: 'fecha', desc: true }}
      storageKey="pedidos"
    />
  );
}

// ─────────────────────────────────────────────────────────────────────
// Modal "Enviar email" con plantillas
// ─────────────────────────────────────────────────────────────────────

function applyPlaceholders(text: string, p: ProveedorFull): string {
  return text
    .replace(/\{\{\s*proveedor\s*\}\}/gi, p.nombre)
    .replace(/\{\{\s*contacto\s*\}\}/gi, p.contacto_principal ?? '')
    .replace(/\{\{\s*email\s*\}\}/gi, p.email ?? '');
}

// ─────────────────────────────────────────────────────────────────────
// Contactos del proveedor (lista CRUD + marcar principal)
// ─────────────────────────────────────────────────────────────────────

function ContactosCard({ proveedorId, contactos }: { proveedorId: string; contactos: ContactoRow[] }) {
  const qc = useQueryClient();
  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [editing, setEditing] = useState<ContactoRow | null>(null);
  const empty = { nombre: '', cargo: '', email: '', telefono: '', es_principal: false };
  const [draft, setDraft] = useState<typeof empty>(empty);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['proveedor', proveedorId, 'full'] });

  const create = useMutation({
    mutationFn: () => api.post(`/proveedores/${proveedorId}/contactos`, draft),
    onSuccess: () => { invalidate(); setNuevoOpen(false); setDraft(empty); },
  });
  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(`/proveedores/${proveedorId}/contactos/${editing?.id}`, body),
    onSuccess: () => { invalidate(); setEditing(null); setDraft(empty); },
  });
  const togglePrincipal = useMutation({
    mutationFn: (contactoId: string) => api.patch(`/proveedores/${proveedorId}/contactos/${contactoId}`, { es_principal: true }),
    onSuccess: invalidate,
  });
  const borrar = useMutation({
    mutationFn: (contactoId: string) => api.delete(`/proveedores/${proveedorId}/contactos/${contactoId}`),
    onSuccess: invalidate,
  });

  const openEdit = (c: ContactoRow) => {
    setEditing(c);
    setDraft({
      nombre: c.nombre ?? '',
      cargo: c.cargo ?? '',
      email: c.email ?? '',
      telefono: c.telefono ?? '',
      es_principal: c.es_principal,
    });
  };

  return (
    <Card
      title={`Contactos (${contactos.length})`}
      actions={<Button variant="create" size="sm" onClick={() => setNuevoOpen(true)}>+ Nuevo contacto</Button>}
    >
      {contactos.length === 0 ? (
        <p className="text-sm italic text-slate-400">Sin contactos. Añade el principal y los adicionales que necesites.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1.5 text-left">Principal</th>
                <th className="px-2 py-1.5 text-left">Nombre</th>
                <th className="px-2 py-1.5 text-left">Cargo</th>
                <th className="px-2 py-1.5 text-left">Email</th>
                <th className="px-2 py-1.5 text-left">Teléfono</th>
                <th className="px-2 py-1.5 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {contactos.map((c) => (
                <tr key={c.id} className={c.es_principal ? 'bg-emerald-50' : ''}>
                  <td className="px-2 py-1.5">
                    {c.es_principal ? (
                      <Badge color="#10b981">★ Principal</Badge>
                    ) : (
                      <button
                        type="button"
                        onClick={() => togglePrincipal.mutate(c.id)}
                        className="cursor-pointer text-xs text-slate-500 hover:text-slate-800 hover:underline"
                      >Marcar principal</button>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-sm">{c.nombre ?? <span className="text-slate-300">—</span>}</td>
                  <td className="px-2 py-1.5 text-xs">{c.cargo ?? <span className="text-slate-300">—</span>}</td>
                  <td className="px-2 py-1.5 text-xs">
                    {c.email ? <a href={`mailto:${c.email}`} className="cursor-pointer text-sky-600 hover:underline">{c.email}</a> : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-2 py-1.5 text-xs">{c.telefono ?? <span className="text-slate-300">—</span>}</td>
                  <td className="px-2 py-1.5 text-right">
                    <button
                      type="button"
                      onClick={() => openEdit(c)}
                      className="mr-1 cursor-pointer rounded px-1.5 py-0.5 text-xs text-amber-700 hover:bg-amber-50"
                    >✏️</button>
                    <button
                      type="button"
                      onClick={async () => { if (await confirmar({ title: '¿Borrar este contacto?', confirmLabel: 'Borrar', danger: true })) borrar.mutate(c.id); }}
                      className="cursor-pointer rounded px-1.5 py-0.5 text-xs text-rose-700 hover:bg-rose-50"
                    >×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={nuevoOpen || editing != null}
        onClose={() => { setNuevoOpen(false); setEditing(null); setDraft(empty); }}
        title={editing ? 'Editar contacto' : 'Nuevo contacto'}
        footer={
          <>
            <Button variant="danger" onClick={() => { setNuevoOpen(false); setEditing(null); setDraft(empty); }}>Cancelar</Button>
            <Button
              variant="save"
              onClick={() => editing ? update.mutate({ ...draft }) : create.mutate()}
              disabled={create.isPending || update.isPending}
            >
              {create.isPending || update.isPending ? 'Guardando…' : 'Guardar'}
            </Button>
          </>
        }
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <FormRow label="Nombre"><Input value={draft.nombre} onChange={(e) => setDraft((d) => ({ ...d, nombre: e.target.value }))} /></FormRow>
          <FormRow label="Cargo"><Input value={draft.cargo} onChange={(e) => setDraft((d) => ({ ...d, cargo: e.target.value }))} placeholder="Sales manager, owner…" /></FormRow>
          <FormRow label="Email"><Input value={draft.email} onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))} /></FormRow>
          <FormRow label="Teléfono"><Input value={draft.telefono} onChange={(e) => setDraft((d) => ({ ...d, telefono: e.target.value }))} /></FormRow>
          <div className="sm:col-span-2">
            <FormRow label="Principal" hint="Solo un contacto puede ser principal por proveedor.">
              <label className="inline-flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={draft.es_principal}
                  onChange={(e) => setDraft((d) => ({ ...d, es_principal: e.target.checked }))}
                  className="h-4 w-4 cursor-pointer"
                />
                <span className="text-sm">Marcar como contacto principal</span>
              </label>
            </FormRow>
          </div>
        </div>
      </Modal>
    </Card>
  );
}

function EmailModal({ proveedor, open, onClose }: { proveedor: ProveedorFull; open: boolean; onClose: () => void }) {
  const templates = useQuery<{ data: EmailTemplate[] }>({
    queryKey: ['email-templates', 'activos'],
    queryFn: () => api.get<{ data: EmailTemplate[] }>('/admin/email-templates?activo=true'),
    enabled: open,
  });

  const [seleccionada, setSeleccionada] = useState<string>('');
  const [asunto, setAsunto] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [destinatario, setDestinatario] = useState(proveedor.email ?? '');
  const [ccCsv, setCcCsv] = useState((proveedor.emails_cc ?? []).join(', '));

  const lista = templates.data?.data ?? [];

  const aplicarPlantilla = (templateId: string) => {
    setSeleccionada(templateId);
    const t = lista.find((x) => x.id === templateId);
    if (!t) return;
    setAsunto(applyPlaceholders(t.asunto, proveedor));
    setCuerpo(applyPlaceholders(t.cuerpo, proveedor));
    if (t.destinatario_default && !destinatario) setDestinatario(t.destinatario_default);
    if (t.emails_cc_default?.length > 0) {
      const merged = new Set([...(proveedor.emails_cc ?? []), ...t.emails_cc_default]);
      setCcCsv(Array.from(merged).join(', '));
    }
  };

  const mailtoUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (asunto) params.set('subject', asunto);
    if (cuerpo) params.set('body', cuerpo);
    const cc = ccCsv.split(/[,;]/).map((s) => s.trim()).filter(Boolean).join(',');
    if (cc) params.set('cc', cc);
    return `mailto:${encodeURIComponent(destinatario)}?${params.toString()}`;
  }, [destinatario, ccCsv, asunto, cuerpo]);

  const abrirCliente = () => {
    window.location.href = mailtoUrl;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Enviar email a ${proveedor.nombre}`}
      size="xl"
      footer={
        <>
          <Button variant="danger" onClick={onClose}>Cancelar</Button>
          <Button variant="create" onClick={abrirCliente} disabled={!destinatario}>Abrir cliente de email</Button>
        </>
      }
    >
      <div className="space-y-3">
        <FormRow label="Plantilla" hint="Opcional — preselecciona asunto, cuerpo y CC. Puedes editar todo antes de enviar.">
          <Select
            options={lista.map((t) => ({ value: t.id, label: t.nombre }))}
            value={seleccionada}
            emptyLabel="— sin plantilla —"
            onChange={(e) => aplicarPlantilla(e.target.value)}
          />
        </FormRow>
        <FormRow label="Destinatario" required>
          <Input value={destinatario} onChange={(e) => setDestinatario(e.target.value)} />
        </FormRow>
        <FormRow label="CC (separados por coma)">
          <Input value={ccCsv} onChange={(e) => setCcCsv(e.target.value)} />
        </FormRow>
        <FormRow label="Asunto">
          <Input value={asunto} onChange={(e) => setAsunto(e.target.value)} />
        </FormRow>
        <FormRow label="Cuerpo">
          <Textarea rows={10} value={cuerpo} onChange={(e) => setCuerpo(e.target.value)} />
        </FormRow>
        <p className="text-xs text-slate-500">
          Al pulsar "Abrir cliente de email" se abrirá tu cliente por defecto (Gmail web, Outlook…) con todos los campos pre-rellenados.
        </p>
      </div>
    </Modal>
  );
}
